/**
 * Simple markdown renderer for bot messages
 * Handles: **bold**, numbered and bulleted lists, line breaks, and in-app links:
 * [text](/path) opens a NovaBotX page, [text](nb:action) runs a chat action (tour, feedback, demo...).
 * Only those two link forms are rendered; anything else stays plain text.
 */

const IN_APP_LINK_RE = /\[([^\]\n]{1,60})\]\((\/[a-z0-9\-/]*|nb:[a-z-]+)\)/gi;

export function renderMarkdown(text) {
  if (!text) return '';
  
  // Use placeholders to protect our generated HTML during escaping
  const placeholders = [];
  let placeholderIndex = 0;

  // Step 0: Pull out in-app links; the markers survive escaping and are restored at the end.
  const links = [];
  text = text.replace(IN_APP_LINK_RE, (match, label, target) => {
    links.push(`<button type="button" class="nb-inline-action" data-nb-target="${escapeHtml(target)}">${escapeHtml(label)}</button>`);
    return `__NBLINK_${links.length - 1}__`;
  });
  
  // Step 1: Convert numbered lists (1. **text**: or 1. text)
  let html = text.split('\n').map(line => {
    const trimmed = line.trim();
    const numberedMatch = trimmed.match(/^(\d+)\.\s+(.+)$/) || trimmed.match(/^([-•])\s+(.+)$/);
    if (numberedMatch) {
      const number = /^\d+$/.test(numberedMatch[1]) ? `${numberedMatch[1]}.` : '•';
      let content = numberedMatch[2];
      // Process bold BEFORE escaping (extract bold content, escape separately)
      const boldParts = [];
      let boldPartIndex = 0;
      content = content.replace(/\*\*(.+?)\*\*/g, (match, boldText) => {
        const marker = `__BOLD_PART_${boldPartIndex}__`;
        boldParts[boldPartIndex] = escapeHtml(boldText);
        boldPartIndex++;
        return marker;
      });
      // Escape the remaining content
      content = escapeHtml(content);
      // Restore bold parts as <strong> tags
      boldParts.forEach((escapedBold, index) => {
        content = content.replace(`__BOLD_PART_${index}__`, `<strong>${escapedBold}</strong>`);
      });
      const placeholder = `__PLACEHOLDER_${placeholderIndex}__`;
      placeholders[placeholderIndex] = `<div class="nova-list-item"><span class="nova-list-number">${number}</span> <span class="nova-list-content">${content}</span></div>`;
      placeholderIndex++;
      return placeholder;
    }
    return line;
  }).join('\n');
  
  // Step 2: Process bold text in remaining content (before escaping)
  const boldMarker = '__BOLD_MARKER__';
  const boldContent = [];
  let boldIndex = 0;
  
  html = html.replace(/\*\*(.+?)\*\*/g, (match, content) => {
    const marker = `${boldMarker}${boldIndex}${boldMarker}`;
    boldContent[boldIndex] = escapeHtml(content);
    boldIndex++;
    return marker;
  });
  
  // Step 3: Escape HTML for security
  html = escapeHtml(html);
  
  // Step 4: Restore bold markers as <strong> tags
  boldContent.forEach((escapedContent, index) => {
    html = html.replace(`${boldMarker}${index}${boldMarker}`, `<strong>${escapedContent}</strong>`);
  });
  
  // Step 5: Restore list item placeholders (our safe HTML)
  placeholders.forEach((safeHtml, index) => {
    html = html.replace(`__PLACEHOLDER_${index}__`, safeHtml);
  });
  
  // Step 6: Handle paragraphs and line breaks
  const paragraphs = html.split('\n\n');
  html = paragraphs.map(para => {
    const trimmed = para.trim();
    if (!trimmed) return '';
    
    // If it contains our list items, return as-is
    if (trimmed.includes('nova-list-item')) {
      return trimmed;
    }
    
    // Otherwise, convert line breaks to <br> and wrap in <p>
    const withBreaks = trimmed.replace(/\n/g, '<br>');
    return `<p>${withBreaks}</p>`;
  }).filter(p => p).join('');
  
  // Clean up
  html = html.replace(/<p>(<div class="nova-list-item">.*?<\/div>)<\/p>/g, '$1');
  html = html.replace(/<p><\/p>/g, '');

  // Restore in-app links; a line of only links becomes a row of action buttons.
  html = html.replace(/<p>((?:__NBLINK_\d+__\s*)+)<\/p>/g, '<div class="nb-inline-actions">$1</div>');
  html = html.replace(/__NBLINK_(\d+)__/g, (match, index) => links[Number(index)] || '');

  return html;
}

/**
 * Escape HTML to prevent XSS
 */
export function escapeHtml(text) {
  const div = document.createElement('div');
  div.textContent = text;
  return div.innerHTML;
}

